import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Duration } from "luxon";
import { Observable, Subject, Subscription, debounceTime, merge } from "rxjs";
import { ISearch } from "./v-search.interface";

/**
 * Searchbar, which allows filtering values based on user input.
 * 
 * @name    Searchbar
 * @tags    Input
 * @remark  Search is debounced.
 * @remark  Add implement ISearch<T> to your component to get type-safe filtering.
 */
@Component({
    selector:      'v-search',
    templateUrl:   './v-search.component.haml',
    styleUrls:     ['./v-search.component.sass'],
    providers:     [{ provide: NG_VALUE_ACCESSOR, useExisting: VSearchComponent, multi: true }],
    encapsulation: ViewEncapsulation.None,
    standalone:    false
})
export class VSearchComponent<T = unknown> implements ISearch<T>, ControlValueAccessor, OnInit, OnDestroy {
    /**
     * Debounce time.
     * @value Duration
     * @default {milliseconds: 250}
     */
    @Input() public debounceTime: Duration = Duration.fromObject({milliseconds: 250});
    /**
     * Filter function.
     * The search term is passed as whole, along with the data. Should return an sub-array of the inputted data.
     *  
     * @example 
     * ```ts
     * const filter = (term: string, arr: T[]) => {
     *      const words = term?.toLowerCase().split(" ");
     *      if (!words?.length) return arr;
     *      return arr.filter(x => words.every(w => x.title.toLowerCase().includes(w)))
     * }
     * ```
     */
    @Input() public filter: (term: string, arr: T[]) => T[];
    /**
     * Should the component request the focus on creation.
     */
    @Input() public autoFocus: boolean;
    /**
     * Manually control the loading spinner
     * @remark This binding is usually not used.
     */
    @Input() public loading: boolean;
    @Input() public value: T[];
    /**
     * Placeholder
     */
    @Input() public placeholder: string = "Suche";
    private _data: T[];
    public get data(): T[] {
        return this._data;
    }

    /**
     * The data array. 
     * Pass the data to be filtered into this binding.
     */
    @Input()
    public set data(value: T[]) {
        this._data = value;
        if (this.filter)this._searchSubjectNoDebounce.next(this.searchValue);
    }
    @Output() public filtered: EventEmitter<T[]> = new EventEmitter();

    private _searchValue: string;
    private _searchSubject: Subject<string> = new Subject<string>();
    private _searchSubjectNoDebounce: Subject<string> = new Subject<string>();
    private _searchObservable: Observable<T[]> = merge(
        this._searchSubjectNoDebounce,
        this._searchSubject.pipe(debounceTime(this.debounceTime.toMillis()))
    ).pipe(this.search());
    private _searchSubscription: Subscription;

    public onChange = (_value: T[]) => {};
    public onTouch = () => {};

    constructor() {
        this._searchSubscription = this._searchObservable.subscribe(x => void this.writeValue(x).catch(e => e.displayErrorToast?.()));
    }

    /**
     * The search term. Ususally inputted from the input field.
     */
    @Input()
    public set searchValue(value: string) {
        this.loading = true;
        this._searchValue = value;
        this._searchSubject.next(value);
        this.searchValueChange.emit(value);
    }

    public get searchValue(): string {
        return this._searchValue;
    }
    // TODO: document output
    @Output()
    public searchValueChange = new EventEmitter<string>();

    public clear(){
        this.searchValue = '';
    }

    public registerOnChange(fn: ((val: T[]) => void)): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: (() => void)): void {
        this.onTouch = fn;
    }

    public update() {
        this._searchSubject.next(this.searchValue);
    }

    public async writeValue(val: T[]) {
        // angular setup control might overwrite value with `null/undefined`.
        // in that case we try filtering the data manually
        if (!val?.length && !this.searchValue?.length) val = await Promise.resolve(this.filter('', this.data));
        if (this.value === val || !val) return;
        this.value = val;
        this.emitValue(this.value);
    }

    private emitValue(values: T[]) {
        this.filtered?.emit(values);
        this.onChange?.(values);
    }

    private search(){
        const self = this;
        return (source: Observable<string>): Observable<T[]> =>
        new Observable(subscriber => {
            source.subscribe({
            next(value) {
                Promise.resolve(self.filter(value, self.data))
                    .then(result => subscriber.next(result))
                    .catch((error) => subscriber.error(error))
                    .finally(() => self.loading = false);
            },
            error(error) {
                subscriber.error(error);
            },
            complete() {
                subscriber.complete();
            }
            });
        });
    }

    public ngOnInit(): void {
        this._searchSubjectNoDebounce.next(this.searchValue);
    }

    public ngOnDestroy(): void {
        this._searchSubscription?.unsubscribe();
    }
}
